import { useState } from "react";
import { X, Upload, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const statusOptions = ["Student", "Working Professional", "Freelancer", "Other"];
const hearAboutOptions = [
  "Instagram / Social Media",
  "LinkedIn",
  "Friend / Referral",
  "College",
  "Website",
  "Event",
  "Other",
];
const resumeRequiredDomains = ["Marketing", "Designing", "SDE & IT Support"];

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1]);
    reader.onerror = () => reject(new Error("Could not read the resume file."));
    reader.readAsDataURL(file);
  });

export default function ApplicationForm({ domain, onClose }) {
  const [currentStep, setCurrentStep] = useState("form"); // "form" or "success"
  const [loading, setLoading] = useState(false);
  const [resume, setResume] = useState(null);
  const [resumeFile, setResumeFile] = useState(null);
  const [applicationId, setApplicationId] = useState(null);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    mobileNumber: "",
    city: "",
    currentStatus: "",
    yearsOfExperience: "",
    linkedinProfile: "",
    hearAbout: "",
    whyJoin: "",
  });

  const [errors, setErrors] = useState({});

  const isResumeRequired = resumeRequiredDomains.includes(domain.name);
  const displayLabel = domain.displayName || domain.name;
  const isGraphicDesign = domain.name === "Designing";

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for mobile number - only allow digits
    if (name === "mobileNumber") {
      const digitsOnly = value.replace(/\D/g, "");
      setFormData((prev) => ({
        ...prev,
        [name]: digitsOnly,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
    
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleResumeChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!validTypes.includes(file.type)) {
      toast.error("Please upload a PDF, DOC, or DOCX file");
      return;
    }

    if (file.size === 0) {
      toast.error("This file appears to be empty. Please choose a different file.");
      return;
    }

    if (file.size > maxSize) {
      toast.error("File size must be less than 5MB");
      return;
    }

    setResume(file.name);
    setResumeFile(file);
    if (errors.resume) {
      setErrors((prev) => ({
        ...prev,
        resume: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) newErrors.fullName = "Full name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = "Invalid email format";
    if (!formData.mobileNumber.trim())
      newErrors.mobileNumber = "Mobile number is required";
    if (!/^\d{10}$/.test(formData.mobileNumber))
      newErrors.mobileNumber = "Mobile number must be exactly 10 digits";
    if (!formData.currentStatus)
      newErrors.currentStatus = "Current status is required";
    if (!formData.hearAbout) newErrors.hearAbout = "Please select how you heard about us";
    if (isResumeRequired && !resumeFile) {
      newErrors.resume = "Resume is required for this domain";
    }
    if (
      isGraphicDesign &&
      formData.linkedinProfile.trim() &&
      !/^https?:\/\/.+\..+/.test(formData.linkedinProfile.trim())
    ) {
      newErrors.linkedinProfile = "Please enter a valid portfolio URL";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    const endpoint = process.env.REACT_APP_GOOGLE_APPS_SCRIPT_URL;
    if (!endpoint) {
      toast.error("Application submission is not configured. Please contact the site administrator.");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        fullName: formData.fullName.trim(),
        email: formData.email.trim(),
        mobileNumber: formData.mobileNumber,
        city: formData.city.trim(),
        domain: domain.name,
        currentStatus: formData.currentStatus,
        yearsOfExperience: formData.yearsOfExperience.trim(),
        linkedinProfile: formData.linkedinProfile.trim(),
        hearAbout: formData.hearAbout,
        whyJoin: formData.whyJoin.trim(),
      };

      if (resumeFile) {
        payload.resume = {
          fileName: resumeFile.name,
          mimeType: resumeFile.type,
          base64: await fileToBase64(resumeFile),
        };
      }

      // Sent as a plain-text body (no custom headers) so the browser doesn't
      // issue a CORS preflight — Apps Script web apps can't respond to OPTIONS.
      let response;
      try {
        response = await fetch(endpoint, {
          method: "POST",
          body: JSON.stringify(payload),
        });
      } catch (networkError) {
        // fetch() rejects with an opaque TypeError for DNS/offline/CORS
        // failures — normalize it instead of showing that raw browser text.
        throw new Error("Something went wrong while submitting your application. Please try again.");
      }

      if (!response.ok) {
        throw new Error("Server responded with an error. Please try again.");
      }

      let result;
      try {
        result = await response.json();
      } catch (parseError) {
        throw new Error("Something went wrong while submitting your application. Please try again.");
      }

      if (!result.success) {
        throw new Error(
          result.message || "Something went wrong while submitting your application. Please try again."
        );
      }

      setApplicationId(result.applicationId || null);
      setCurrentStep("success");
      toast.success("Application submitted successfully!");
    } catch (error) {
      console.error("Submission error:", error);
      toast.error(
        error.message || "Something went wrong while submitting your application. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBackToWebsite = () => {
    window.location.href = "/";
  };

  if (currentStep === "success") {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
        <div className="glass rounded-2xl border border-[var(--ee-border)] p-8 md:p-12 max-w-md w-full text-center animate-in fade-in duration-300">
          <div className="w-16 h-16 rounded-full bg-[var(--ee-gold)]/10 flex items-center justify-center mx-auto mb-6 border border-[var(--ee-gold)]/30">
            <CheckCircle size={32} className="text-[var(--ee-gold)]" />
          </div>

          <h2 className="font-display text-2xl font-semibold mb-3">
            Application Submitted Successfully!
          </h2>

          <p className="text-neutral-300 mb-8">
            Thank you for your interest in joining Elite Eventz. We&apos;ve
            received your application and our team will review your profile.
            If it matches an opportunity, we&apos;ll get in touch with you at{" "}
            <span className="text-[var(--ee-gold)]">{formData.email}</span>.
          </p>

          <div className="bg-black/30 rounded-xl p-4 mb-8 text-sm text-neutral-300 space-y-2">
            <div>
              <p className="font-semibold text-neutral-100 mb-1">Domain Applied:</p>
              <p>{displayLabel}</p>
            </div>
            {applicationId && (
              <div>
                <p className="font-semibold text-neutral-100 mb-1">Application ID:</p>
                <p>{applicationId}</p>
              </div>
            )}
          </div>

          <button
            onClick={handleBackToWebsite}
            className="btn-gold w-full"
          >
            Back to Elite Eventz
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 overflow-y-auto py-10">
      <div className="glass rounded-2xl border border-[var(--ee-border)] p-8 md:p-12 max-w-2xl w-full my-auto animate-in fade-in duration-300">
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="eyebrow mb-2">Application Form</div>
            <h2 className="font-display text-3xl font-semibold">
              Apply for <span className="gold-text-gradient">{displayLabel}</span>
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-white transition p-1"
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>

        {isResumeRequired && (
          <div className="bg-[var(--ee-gold)]/10 border border-[var(--ee-gold)]/30 rounded-lg p-4 mb-8">
            <p className="text-sm text-neutral-200">
              ℹ️ Resume is required for {displayLabel} applicants.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Information Section */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-300 mb-4">
              Personal Information
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  placeholder="Your full name"
                  className={`w-full px-4 py-2.5 rounded-lg bg-black/30 border ${
                    errors.fullName
                      ? "border-red-500"
                      : "border-[var(--ee-border)]"
                  } text-neutral-100 placeholder-neutral-500 focus:outline-none focus:border-[var(--ee-gold)] transition`}
                />
                {errors.fullName && (
                  <p className="text-red-400 text-xs mt-1">{errors.fullName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your@email.com"
                  className={`w-full px-4 py-2.5 rounded-lg bg-black/30 border ${
                    errors.email
                      ? "border-red-500"
                      : "border-[var(--ee-border)]"
                  } text-neutral-100 placeholder-neutral-500 focus:outline-none focus:border-[var(--ee-gold)] transition`}
                />
                {errors.email && (
                  <p className="text-red-400 text-xs mt-1">{errors.email}</p>
                )}
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">
                    Mobile Number *
                  </label>
                  <input
                    type="tel"
                    name="mobileNumber"
                    value={formData.mobileNumber}
                    onChange={handleInputChange}
                    placeholder="+91 XXXXXXXXXX"
                    className={`w-full px-4 py-2.5 rounded-lg bg-black/30 border ${
                      errors.mobileNumber
                        ? "border-red-500"
                        : "border-[var(--ee-border)]"
                    } text-neutral-100 placeholder-neutral-500 focus:outline-none focus:border-[var(--ee-gold)] transition`}
                  />
                  {errors.mobileNumber && (
                    <p className="text-red-400 text-xs mt-1">
                      {errors.mobileNumber}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">
                    City / Location
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="Your city"
                    className="w-full px-4 py-2.5 rounded-lg bg-black/30 border border-[var(--ee-border)] text-neutral-100 placeholder-neutral-500 focus:outline-none focus:border-[var(--ee-gold)] transition"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Professional Information Section */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-300 mb-4">
              Professional Information
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">
                  Current Status *
                </label>
                <select
                  name="currentStatus"
                  value={formData.currentStatus}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2.5 rounded-lg bg-black/30 border ${
                    errors.currentStatus
                      ? "border-red-500"
                      : "border-[var(--ee-border)]"
                  } text-neutral-100 focus:outline-none focus:border-[var(--ee-gold)] transition appearance-none cursor-pointer option:bg-black option:text-neutral-100`}
                >
                  <option value="">Select your status</option>
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
                {errors.currentStatus && (
                  <p className="text-red-400 text-xs mt-1">
                    {errors.currentStatus}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">
                  Years of Experience
                </label>
                <input
                  type="text"
                  name="yearsOfExperience"
                  value={formData.yearsOfExperience}
                  onChange={handleInputChange}
                  placeholder="e.g., 2 years"
                  className="w-full px-4 py-2.5 rounded-lg bg-black/30 border border-[var(--ee-border)] text-neutral-100 placeholder-neutral-500 focus:outline-none focus:border-[var(--ee-gold)] transition"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">
                  {isGraphicDesign ? "Portfolio Link" : "LinkedIn Profile"}
                </label>
                <input
                  type="url"
                  name="linkedinProfile"
                  value={formData.linkedinProfile}
                  onChange={handleInputChange}
                  placeholder={
                    isGraphicDesign
                      ? "https://yourportfolio.com"
                      : "https://linkedin.com/in/yourprofile"
                  }
                  className={`w-full px-4 py-2.5 rounded-lg bg-black/30 border ${
                    errors.linkedinProfile
                      ? "border-red-500"
                      : "border-[var(--ee-border)]"
                  } text-neutral-100 placeholder-neutral-500 focus:outline-none focus:border-[var(--ee-gold)] transition`}
                />
                {errors.linkedinProfile && (
                  <p className="text-red-400 text-xs mt-1">{errors.linkedinProfile}</p>
                )}
              </div>


            </div>
          </div>

          {/* Resume Upload Section */}
          {isResumeRequired && (
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-300 mb-4">
                Resume Upload
              </h3>
              <div className="relative">
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleResumeChange}
                  className="hidden"
                  id="resume-upload"
                />
                <label
                  htmlFor="resume-upload"
                  className={`flex items-center justify-center gap-3 p-6 rounded-lg border-2 border-dashed ${
                    errors.resume
                      ? "border-red-500"
                      : "border-[var(--ee-border)]"
                  } cursor-pointer hover:border-[var(--ee-gold)] hover:bg-[var(--ee-gold)]/5 transition`}
                >
                  <Upload size={20} className="text-[var(--ee-gold)]" />
                  <div className="text-center">
                    <p className="text-sm font-medium text-neutral-200">
                      {resume ? resume : "Click to upload resume"}
                    </p>
                    <p className="text-xs text-neutral-400 mt-1">
                      PDF, DOC or DOCX (Max 5MB)
                    </p>
                  </div>
                </label>
                {errors.resume && (
                  <p className="text-red-400 text-xs mt-1">{errors.resume}</p>
                )}
              </div>
            </div>
          )}

          {/* Additional Questions Section */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-300 mb-4">
              Additional Information
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">
                  How did you hear about Elite Eventz? *
                </label>
                <select
                  name="hearAbout"
                  value={formData.hearAbout}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2.5 rounded-lg bg-black/30 border ${
                    errors.hearAbout
                      ? "border-red-500"
                      : "border-[var(--ee-border)]"
                  } text-neutral-100 focus:outline-none focus:border-[var(--ee-gold)] transition appearance-none cursor-pointer option:bg-black option:text-neutral-100`}
                >
                  <option value="">Select an option</option>
                  {hearAboutOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                {errors.hearAbout && (
                  <p className="text-red-400 text-xs mt-1">{errors.hearAbout}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">
                  Why would you like to join Elite Eventz?
                </label>
                <textarea
                  name="whyJoin"
                  value={formData.whyJoin}
                  onChange={handleInputChange}
                  placeholder="Share your thoughts..."
                  rows={4}
                  className="w-full px-4 py-2.5 rounded-lg bg-black/30 border border-[var(--ee-border)] text-neutral-100 placeholder-neutral-500 focus:outline-none focus:border-[var(--ee-gold)] transition resize-none"
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-2.5 rounded-lg border border-[var(--ee-border)] text-neutral-300 hover:text-neutral-100 font-medium transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 btn-gold font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Submitting..." : "Submit Application"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
